// Unified view header. Replaces ad-hoc decorative gradient banners.
// Brand stripe (left edge) is the only chromatic signal; everything else is
// neutral surface so it themes. `tone` prop is for views whose data axis carries
// semantic color (R-Lean = partisan-r, DropOff = danger, etc.).
import React from 'react';

export type ViewHeaderTone = 'accent' | 'partisan-r' | 'partisan-d' | 'success' | 'warning' | 'danger' | 'info';

interface ViewHeaderProps {
  title: React.ReactNode;
  tagline?: React.ReactNode;
  inline?: React.ReactNode;   // sits next to title (LiveBadge, InfoTip)
  actions?: React.ReactNode;  // right-aligned action buttons
  icon?: React.ReactNode;
  tone?: ViewHeaderTone;
  testId?: string;
}

const TONE_STRIPE: Record<ViewHeaderTone, string> = {
  // The default stripe is the brand's propensity ramp read vertically —
  // cold at the base, the tenant's accent lit at the top. Built from the
  // accent TOKEN so a tenant with a non-red accent stays coherent; the
  // semantic tones below stay flat because their color IS the message.
  accent: 'bg-accent [background-image:linear-gradient(to_top,#2E3644,rgb(var(--accent)))]',
  'partisan-r': 'bg-partisan-r',
  'partisan-d': 'bg-partisan-d',
  success: 'bg-status-success-bg',
  warning: 'bg-status-warning-bg',
  danger: 'bg-status-danger-bg',
  info: 'bg-status-info-bg',
};

export const ViewHeader: React.FC<ViewHeaderProps> = ({ title, tagline, inline, actions, icon, tone = 'accent', testId }) => (
  <header data-testid={testId} className="rounded-lg bg-bg-raised border border-border-base overflow-hidden flex items-stretch shadow-ds-sm">
    <div className={`w-1.5 sm:w-2 shrink-0 ${TONE_STRIPE[tone]}`} aria-hidden="true" />
    <div className="flex-1 px-5 sm:px-6 py-4 flex items-center justify-between flex-wrap gap-3">
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          {icon && <span className="text-accent-fg flex-shrink-0" aria-hidden="true">{icon}</span>}
          <h1 className="text-2xl sm:text-3xl font-bold text-fg-primary leading-tight break-words min-w-0">{title}</h1>
          {inline}
        </div>
        {tagline && <p className="text-fg-secondary text-sm mt-1">{tagline}</p>}
      </div>
      {actions && <div className="flex items-center gap-2 flex-shrink-0">{actions}</div>}
    </div>
  </header>
);
